import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import AppLayout from "@/components/AppLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { ArrowRight, ClipboardList, DollarSign, Mail, Megaphone, PhoneCall, Plus, Rocket, Send, Sparkles, Target, TrendingUp, Users, Zap } from "lucide-react";
import { CreateLeadDialog } from "@/components/sales/CreateLeadDialog";
import AICampaignGenerator from "@/components/sales/AICampaignGenerator";
import { AnimatedStatCard } from "@/components/sales/AnimatedStatCard";
import { ActivityFeed } from "@/components/sales/ActivityFeed";
import { PipelineMetricsCards } from "@/components/sales/PipelineMetricsCards";
import { AIAssistantPanel } from "@/components/sales/AIAssistantPanel";
import { EmailComposeDialog } from "@/components/email/EmailComposeDialog";
import { LeadCard } from "@/components/leads/LeadCard";
import { LeadDetailPanel } from "@/components/sales/LeadDetailPanel";
import { SequencesDialog } from "@/components/sales/SequencesDialog";
import { DoorKnockerQuickQuote } from "@/components/leads/DoorKnockerQuickQuote";
import { cn } from "@/lib/utils";
interface StatsData {
  totalLeads: number;
  activeSequences: number;
  replyRate: number;
  demosScheduled: number;
  converted: number;
}

interface CampaignSummary {
  total: number;
  active: number;
  draft: number;
  approved: number;
  paused: number;
  completed: number;
}

const pipelineStages = [
  { id: 'new', label: 'NEW LEAD', color: 'bg-blue-500', statuses: ['new'] },
  { id: 'contacted', label: 'CONTACTED', color: 'bg-info', statuses: ['contacted'] },
  { id: 'demo_booked', label: 'DEMO BOOKED', color: 'bg-purple-500', statuses: ['qualified'] },
  { id: 'proposal_sent', label: 'PROPOSAL SENT', color: 'bg-orange-500', statuses: ['proposal_sent', 'negotiating'] },
  { id: 'won', label: 'WON', color: 'bg-success', statuses: ['won'] },
];
const SalesCommandCenter = () => {
  const navigate = useNavigate();
  const {
    user,
    orgId,
    loading: authLoading
  } = useAuth();
  const [loading, setLoading] = useState(true);
  const [allLeads, setAllLeads] = useState<any[]>([]);
  const [pipelineLeads, setPipelineLeads] = useState<Record<string, any[]>>({
    new: [],
    contacted: [],
    demo_booked: [],
    proposal_sent: [],
    won: [],
  });
  const [convertingLeadId, setConvertingLeadId] = useState<string | null>(null);
  const [pendingConvertLeadId, setPendingConvertLeadId] = useState<string | null>(null);
  const [stats, setStats] = useState<StatsData>({
    totalLeads: 0,
    activeSequences: 0,
    replyRate: 0,
    demosScheduled: 0,
    converted: 0
  });
  const [campaignSummary, setCampaignSummary] = useState<CampaignSummary>({
    total: 0,
    active: 0,
    draft: 0,
    approved: 0,
    paused: 0,
    completed: 0,
  });
  const [createLeadOpen, setCreateLeadOpen] = useState(false);
  const [aiCampaignOpen, setAiCampaignOpen] = useState(false);
  const [aiAssistantOpen, setAiAssistantOpen] = useState(false);
  const [emailComposeDialogOpen, setEmailComposeDialogOpen] = useState(false);
  const [selectedLeadId, setSelectedLeadId] = useState<string | null>(null);
  const [leadPanelOpen, setLeadPanelOpen] = useState(false);
  const [draggedLeadId, setDraggedLeadId] = useState<string | null>(null);
  const [sequencesDialogOpen, setSequencesDialogOpen] = useState(false);
  const [selectedLeadIds, setSelectedLeadIds] = useState<Set<string>>(new Set());
  useEffect(() => {
    if (!authLoading && user && orgId) {
      loadData();
    } else if (!authLoading && !user) {
      navigate("/auth");
    }
  }, [authLoading, user, orgId]);
  const loadData = async () => {
    setLoading(true);
    try {
      if (!orgId) return;

      // Load leads
      const {
        data: leads,
        error: leadsError
      } = await supabase.from('leads').select('*').eq('org_id', orgId).order('created_at', {
        ascending: false
      });
      if (leadsError) throw leadsError;
      setAllLeads(leads || []);

      // Categorize leads by pipeline stage
      const categorized: Record<string, any[]> = {
        new: [],
        contacted: [],
        demo_booked: [],
        proposal_sent: [],
        won: [],
      };

      (leads || []).forEach(lead => {
        if (lead.status === 'new') categorized.new.push(lead);
        else if (lead.status === 'contacted') categorized.contacted.push(lead);
        else if (lead.status === 'qualified') categorized.demo_booked.push(lead);
        else if (['proposal_sent', 'negotiating'].includes(lead.status)) categorized.proposal_sent.push(lead);
        else if (lead.status === 'won') categorized.won.push(lead);
      });

      setPipelineLeads(categorized);

      // Calculate stats
      const totalLeads = leads?.length || 0;
      const converted = leads?.filter(l => l.status === 'won').length || 0;
      const demosScheduled = leads?.filter(l => l.status === 'contacted' || l.status === 'qualified').length || 0;

      // Load campaigns for active sequences
      const {
        data: campaigns
      } = await supabase.from('ai_campaigns').select('id, status').eq('org_id', orgId);
      const activeSequences = campaigns?.filter((campaign) => campaign.status === 'active').length || 0;
      setCampaignSummary({
        total: campaigns?.length || 0,
        active: activeSequences,
        draft: campaigns?.filter((campaign) => campaign.status === 'draft').length || 0,
        approved: campaigns?.filter((campaign) => campaign.status === 'approved').length || 0,
        paused: campaigns?.filter((campaign) => campaign.status === 'paused').length || 0,
        completed: campaigns?.filter((campaign) => campaign.status === 'completed').length || 0,
      });

      // Calculate reply rate from campaign email sends
      const {
        data: emailSends
      } = await supabase.from('campaign_email_sends').select('replied_at').eq('org_id', orgId);
      const totalEmails = emailSends?.length || 0;
      const replied = emailSends?.filter(e => e.replied_at).length || 0;
      const replyRate = totalEmails > 0 ? Math.round(replied / totalEmails * 100) : 0;
      setStats({
        totalLeads,
        activeSequences,
        replyRate,
        demosScheduled,
        converted
      });
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load sales data');
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (leadId: string) => {
    setDraggedLeadId(leadId);
  };

  const handleSelectLead = (leadId: string, checked: boolean) => {
    setSelectedLeadIds(prev => {
      const next = new Set(prev);
      if (checked) next.add(leadId);
      else next.delete(leadId);
      return next;
    });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
  };

  const handleConvertLead = async (leadId: string) => {
    // Open job type selection dialog instead of converting immediately
    setPendingConvertLeadId(leadId);
  };

  const performConvertLead = async (leadId: string, jobType: 'Retail' | 'Insurance' | 'Commercial') => {
    setPendingConvertLeadId(null);
    setConvertingLeadId(leadId);
    try {
      const { data, error } = await supabase.functions.invoke('convert-lead-to-job', {
        body: { leadId, jobType }
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      toast.success(`Lead converted to ${jobType} job`);
      await loadData();
      if (data?.job?.id) navigate(`/jobs/${data.job.id}`);
    } catch (err: any) {
      console.error('Convert lead failed:', err);
      toast.error(err.message || 'Failed to convert lead');
    } finally {
      setConvertingLeadId(null);
    }
  };

  const handleDrop = async (stageId: string) => {
    if (!draggedLeadId) return;
    
    // Map stage ID to actual status value
    const statusMap: Record<string, 'new' | 'contacted' | 'qualified' | 'proposal_sent' | 'negotiating' | 'won' | 'lost'> = {
      'new': 'new',
      'contacted': 'contacted',
      'demo_booked': 'qualified',
      'proposal_sent': 'proposal_sent',
      'won': 'won',
    };
    
    const status = statusMap[stageId];
    if (!status) return;
    
    try {
      const { error } = await supabase
        .from('leads')
        .update({ status })
        .eq('id', draggedLeadId);
      
      if (error) throw error;
      
      toast.success('Lead status updated');
      setDraggedLeadId(null);
      await loadData();
    } catch (error) {
      console.error('Error updating lead:', error);
      toast.error('Failed to update lead status');
    }
  };
  
  return <AppLayout>
      <div className="relative min-h-screen bg-background overflow-hidden">
        {/* Ambient background */}
        <div className="pointer-events-none absolute inset-0 overflow-hidden">
          <div className="absolute -top-40 -left-32 h-[500px] w-[500px] rounded-full bg-primary/10 blur-[120px]" />
          <div className="absolute top-20 right-0 h-[400px] w-[400px] rounded-full bg-purple-500/10 blur-[120px]" />
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_-20%,hsl(var(--primary)/0.08),transparent_60%)]" />
        </div>

        {/* Header */}
        <div className="relative border-b border-border/60 bg-card/70 backdrop-blur-2xl">
          <div className="container mx-auto px-6 py-10">
            <div className="flex items-center justify-between gap-4 flex-wrap">
              <div className="space-y-3">
                <div className="inline-flex items-center gap-2 rounded-full border border-primary/25 bg-primary/10 px-3 py-1.5 backdrop-blur">
                  <div className="relative flex h-2 w-2">
                    <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-primary opacity-75" />
                    <span className="relative inline-flex h-2 w-2 rounded-full bg-primary" />
                  </div>
                  <span className="text-[10px] font-bold uppercase tracking-[0.2em] text-primary">
                    Front desk · Lead intake
                  </span>
                </div>
                <h1 className="text-5xl font-bold tracking-normal text-foreground drop-shadow-sm">
                  Sales Hub
                </h1>
                <p className="text-base text-muted-foreground max-w-xl">
                  Capture new phone leads, route them into follow-up, and keep today’s sales workflow moving.
                </p>
              </div>
              <div className="flex items-center gap-2">
                <DoorKnockerQuickQuote
                  trigger={
                    <Button
                      size="lg"
                      variant="outline"
                      className="gap-2 bg-background/60 backdrop-blur border-primary/30 hover:border-primary/60 hover:bg-primary/5"
                    >
                      <Zap className="h-4 w-4 text-primary" />
                      Instant Quote
                    </Button>
                  }
                />
                <Button
                  size="lg"
                  onClick={() => setCreateLeadOpen(true)}
                  className="gap-2 shadow-lg shadow-primary/20 hover:shadow-primary/30"
                >
                  <Plus className="h-4 w-4" />
                  New Lead
                </Button>
              </div>
            </div>
          </div>
        </div>

        <div className="relative container mx-auto px-6 py-8 space-y-6">
          <div className="grid gap-4 lg:grid-cols-[1.3fr_0.9fr] xl:grid-cols-[1.45fr_0.85fr]">
            <Card className="overflow-hidden border-border/50 bg-card/80 backdrop-blur-xl">
              <CardContent className="p-0">
                <div className="grid min-h-[260px] gap-0 md:grid-cols-[1.1fr_0.9fr]">
                  <div className="flex flex-col justify-between border-b border-border/50 p-6 md:border-b-0 md:border-r">
                    <div className="space-y-4">
                      <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                        <PhoneCall className="h-6 w-6" />
                      </div>
                      <div>
                        <h2 className="text-2xl font-bold tracking-normal text-foreground">Receptionist intake</h2>
                        <p className="mt-2 max-w-md text-sm text-muted-foreground">
                          Add the caller, select the lead, then enroll them into the right campaign or sequence without digging through the campaign workspace.
                        </p>
                      </div>
                    </div>
                    <div className="mt-6 flex flex-wrap gap-2">
                      <Button size="lg" onClick={() => setCreateLeadOpen(true)} className="gap-2">
                        <Plus className="h-4 w-4" />
                        New Lead
                      </Button>
                      <Button size="lg" variant="outline" onClick={() => setSequencesDialogOpen(true)} className="gap-2">
                        <Send className="h-4 w-4" />
                        Enroll in Sequence
                      </Button>
                    </div>
                  </div>
                  <div className="grid gap-3 p-6 sm:grid-cols-2 md:grid-cols-1">
                    <button onClick={() => setAiCampaignOpen(true)} className="group flex items-center justify-between rounded-xl border border-primary/20 bg-primary/10 p-4 text-left transition-all hover:border-primary/40 hover:bg-primary/15">
                      <span>
                        <span className="block text-sm font-semibold text-foreground">Create campaign</span>
                        <span className="mt-1 block text-xs text-muted-foreground">Build a new outreach push</span>
                      </span>
                      <Sparkles className="h-5 w-5 text-primary transition-transform group-hover:scale-110" />
                    </button>
                    <button onClick={() => setSequencesDialogOpen(true)} className="group flex items-center justify-between rounded-xl border border-border/60 bg-background/50 p-4 text-left transition-all hover:border-primary/30 hover:bg-background">
                      <span>
                        <span className="block text-sm font-semibold text-foreground">Sequence library</span>
                        <span className="mt-1 block text-xs text-muted-foreground">Edit follow-up templates</span>
                      </span>
                      <Mail className="h-5 w-5 text-primary transition-transform group-hover:scale-110" />
                    </button>
                    <DoorKnockerQuickQuote
                      trigger={
                        <Button variant="outline" className="h-auto justify-between rounded-xl border-border/60 bg-background/50 p-4 text-left hover:border-primary/30">
                          <span>
                            <span className="block text-sm font-semibold text-foreground">Instant quote</span>
                            <span className="mt-1 block text-xs font-normal text-muted-foreground">Field sales pricing flow</span>
                          </span>
                          <Zap className="h-5 w-5 text-primary" />
                        </Button>
                      }
                    />
                  </div>
                </div>
              </CardContent>
            </Card>

            <button onClick={() => navigate("/campaigns")} className="group overflow-hidden rounded-xl border border-border/50 bg-card/80 text-left text-card-foreground premium-shadow transition-all duration-300 hover:border-primary/30 hover:premium-shadow-md">
              <div className="flex h-full flex-col justify-between p-6">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-primary/20 bg-primary/10 text-primary">
                    <Megaphone className="h-6 w-6" />
                  </div>
                  <ArrowRight className="h-5 w-5 text-muted-foreground transition-transform group-hover:translate-x-1 group-hover:text-primary" />
                </div>
                <div className="mt-8">
                  <div className="text-sm font-semibold text-muted-foreground">Campaign workspace</div>
                  <div className="mt-2 text-4xl font-bold tracking-normal text-foreground">{campaignSummary.total}</div>
                  <div className="mt-1 text-sm text-muted-foreground">campaigns total · {campaignSummary.active} active</div>
                </div>
                <div className="mt-6 grid grid-cols-3 gap-2">
                  <div className="rounded-lg border border-border/60 bg-background/50 p-3">
                    <div className="text-lg font-semibold tabular-nums text-foreground">{campaignSummary.approved}</div>
                    <div className="text-[11px] text-muted-foreground">Approved</div>
                  </div>
                  <div className="rounded-lg border border-border/60 bg-background/50 p-3">
                    <div className="text-lg font-semibold tabular-nums text-foreground">{campaignSummary.draft}</div>
                    <div className="text-[11px] text-muted-foreground">Draft</div>
                  </div>
                  <div className="rounded-lg border border-border/60 bg-background/50 p-3">
                    <div className="text-lg font-semibold tabular-nums text-foreground">{campaignSummary.paused}</div>
                    <div className="text-[11px] text-muted-foreground">Paused</div>
                  </div>
                </div>
              </div>
            </button>
          </div>

          {/* Row 1: Quick Stats */}
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <AnimatedStatCard title="Total Leads" value={stats.totalLeads} icon={Users} gradient="from-blue-500/10 via-transparent to-transparent" iconBg="bg-gradient-to-br from-blue-500 to-blue-600" />
            <AnimatedStatCard title="Active Campaigns" value={stats.activeSequences} icon={Rocket} gradient="from-purple-500/10 via-transparent to-transparent" iconBg="bg-gradient-to-br from-purple-500 to-purple-600" />
            <AnimatedStatCard title="Reply Rate" value={stats.replyRate} icon={TrendingUp} gradient="from-emerald-500/10 via-transparent to-transparent" iconBg="bg-gradient-to-br from-emerald-500 to-emerald-600" />
            <AnimatedStatCard title="Demos Scheduled" value={stats.demosScheduled} icon={Target} gradient="from-orange-500/10 via-transparent to-transparent" iconBg="bg-gradient-to-br from-orange-500 to-orange-600" />
            <AnimatedStatCard title="Converted" value={stats.converted} icon={DollarSign} gradient="from-teal-500/10 via-transparent to-transparent" iconBg="bg-gradient-to-br from-teal-500 to-emerald-600" />
          </div>

          {/* Row 2: Activity Feed & Quick Actions */}
          <div className="grid gap-4 md:grid-cols-2">
            <ActivityFeed />

            <Card className="border-border/40 bg-card/40 backdrop-blur-xl overflow-hidden relative">
              <div className="absolute inset-0 bg-gradient-to-br from-emerald-500/5 via-transparent to-transparent pointer-events-none" />
              <CardHeader className="relative pb-3">
                <div className="flex items-center gap-3">
                  <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-emerald-500 to-emerald-600 flex items-center justify-center shadow-lg shadow-emerald-500/20 ring-1 ring-white/10">
                    <Zap className="h-4 w-4 text-white" />
                  </div>
                  <div>
                    <CardTitle className="text-base font-semibold">Quick Actions</CardTitle>
                    <CardDescription className="text-xs">Launch key workflows</CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="relative">
                <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
                  <button
                    onClick={() => setAiCampaignOpen(true)}
                    className="group/tile flex flex-col items-center gap-2 p-4 rounded-xl border border-primary/30 bg-gradient-to-br from-primary/15 to-primary/5 hover:from-primary/25 hover:to-primary/10 hover:border-primary/60 transition-all hover:-translate-y-0.5"
                  >
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center shadow-lg shadow-primary/30 ring-1 ring-white/10 group-hover/tile:scale-110 transition-transform">
                      <Sparkles className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs font-semibold">AI Campaign</span>
                  </button>
                  <button
                    onClick={() => navigate("/campaigns")}
                    className="group/tile flex flex-col items-center gap-2 p-4 rounded-xl border border-border/60 bg-background/40 hover:border-border hover:bg-background/80 transition-all hover:-translate-y-0.5"
                  >
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-secondary to-primary flex items-center justify-center shadow-lg shadow-primary/20 ring-1 ring-white/10 group-hover/tile:scale-110 transition-transform">
                      <Megaphone className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs font-semibold">Campaigns</span>
                  </button>
                  <button
                    onClick={() => setSequencesDialogOpen(true)}
                    className="group/tile flex flex-col items-center gap-2 p-4 rounded-xl border border-border/60 bg-background/40 hover:border-border hover:bg-background/80 transition-all hover:-translate-y-0.5"
                  >
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-500 flex items-center justify-center shadow-lg shadow-blue-500/20 ring-1 ring-white/10 group-hover/tile:scale-110 transition-transform">
                      <Mail className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs font-semibold">Sequences</span>
                  </button>
                  <button
                    onClick={() => setAiAssistantOpen(true)}
                    className="group/tile flex flex-col items-center gap-2 p-4 rounded-xl border border-border/60 bg-background/40 hover:border-border hover:bg-background/80 transition-all hover:-translate-y-0.5"
                  >
                    <div className="h-10 w-10 rounded-lg bg-gradient-to-br from-orange-500 to-pink-500 flex items-center justify-center shadow-lg shadow-orange-500/20 ring-1 ring-white/10 group-hover/tile:scale-110 transition-transform">
                      <Target className="h-5 w-5 text-white" />
                    </div>
                    <span className="text-xs font-semibold">AI Stats</span>
                  </button>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* Row 3: Pipeline View */}
          <Card className="border-border/40 bg-card/40 backdrop-blur-xl overflow-hidden">
            <CardHeader className="border-b border-border/40">
              <div className="flex items-center justify-between flex-wrap gap-2">
                <div>
                  <CardTitle className="text-base font-semibold">Pipeline Overview</CardTitle>
                  <CardDescription className="text-xs">Drag leads between stages to update their status</CardDescription>
                </div>
                <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
                  {allLeads.length} total · {stats.converted} won
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-4">
              {allLeads.length === 0 && !loading ? (
                <div className="flex flex-col items-center justify-center py-16 px-4 text-center">
                  <div className="h-14 w-14 rounded-2xl bg-primary/10 flex items-center justify-center mb-4 ring-1 ring-primary/20">
                    <Users className="h-7 w-7 text-primary" />
                  </div>
                  <h3 className="text-base font-semibold text-foreground">No leads in the pipeline yet</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-md">
                    Capture your first lead from a phone call, walk-in, or canvassing route. They'll appear here ready to drag through the stages.
                  </p>
                  <div className="flex flex-wrap items-center justify-center gap-2 mt-5">
                    <Button onClick={() => setCreateLeadOpen(true)} className="gap-2">
                      <Plus className="h-4 w-4" />
                      Add first lead
                    </Button>
                    <Button variant="outline" onClick={() => navigate("/canvassing")} className="gap-2">
                      <Target className="h-4 w-4" />
                      Start canvassing
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="flex gap-3 overflow-x-auto pb-2">
                  {pipelineStages.map((stage) => (
                    <div
                      key={stage.id}
                      className="flex-shrink-0 w-72 group/col"
                      onDragOver={handleDragOver}
                      onDrop={() => handleDrop(stage.id)}
                    >
                      <div className="h-full rounded-xl border border-border/40 bg-background/40 backdrop-blur transition-colors group-hover/col:border-border/80">
                        <div className="px-4 pt-4 pb-3">
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <div className={cn("h-2 w-2 rounded-full", stage.color)} />
                              <span className="text-[11px] font-semibold uppercase tracking-[0.12em] text-foreground/80">
                                {stage.label}
                              </span>
                            </div>
                            <Badge variant="secondary" className="bg-muted/60 text-foreground/70 font-medium">
                              {pipelineLeads[stage.id]?.length || 0}
                            </Badge>
                          </div>
                          <div className="h-[2px] rounded-full bg-border/40 overflow-hidden">
                            <div className={cn("h-full rounded-full", stage.color)} style={{ width: `${Math.min(100, (pipelineLeads[stage.id]?.length || 0) * 20)}%` }} />
                          </div>
                        </div>
                        <div className="px-3 pb-3 space-y-2 max-h-[700px] overflow-y-auto">
                          {pipelineLeads[stage.id]?.map((lead) => (
                            <div
                              key={lead.id}
                              draggable
                              onDragStart={() => handleDragStart(lead.id)}
                              className="cursor-move transition-transform hover:-translate-y-0.5"
                            >
                              <LeadCard
                                lead={lead}
                                isSelected={selectedLeadIds.has(lead.id)}
                                onSelect={handleSelectLead}
                                onConvert={handleConvertLead}
                                onCardClick={(id) => {
                                  setSelectedLeadId(id);
                                  setLeadPanelOpen(true);
                                }}
                                isConverting={convertingLeadId === lead.id}
                              />
                            </div>
                          ))}
                          {pipelineLeads[stage.id]?.length === 0 && (
                            <div className="text-center py-10 text-xs text-muted-foreground/60 border border-dashed border-border/40 rounded-lg">
                              Drop leads here
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Dialogs */}
        <CreateLeadDialog open={createLeadOpen} onOpenChange={setCreateLeadOpen} onLeadCreated={loadData} />
        
        <AICampaignGenerator open={aiCampaignOpen} onOpenChange={setAiCampaignOpen} />

        <AIAssistantPanel open={aiAssistantOpen} onOpenChange={setAiAssistantOpen} />

        <EmailComposeDialog 
          open={emailComposeDialogOpen} 
          onOpenChange={setEmailComposeDialogOpen}
        />

        <SequencesDialog
          open={sequencesDialogOpen}
          onOpenChange={setSequencesDialogOpen}
        />

        {/* Lead Detail Drawer */}
        <Sheet open={leadPanelOpen} onOpenChange={setLeadPanelOpen}>
          <SheetContent side="right" className="w-full sm:max-w-2xl p-0">
            <SheetHeader className="sr-only">
              <SheetTitle>Lead Details</SheetTitle>
            </SheetHeader>
            {selectedLeadId && (
              <LeadDetailPanel
                leadId={selectedLeadId}
                onClose={() => setLeadPanelOpen(false)}
                onConvert={async (leadId) => {
                  await handleConvertLead(leadId);
                  setLeadPanelOpen(false);
                }}
                isConverting={convertingLeadId === selectedLeadId}
              />
            )}
          </SheetContent>
        </Sheet>

        <Dialog open={!!pendingConvertLeadId} onOpenChange={(open) => !open && setPendingConvertLeadId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Select Job Type</DialogTitle>
              <DialogDescription>
                What type of job is this? This determines the workflow and tabs available.
              </DialogDescription>
            </DialogHeader>
            <div className="grid grid-cols-1 gap-3 py-4">
              {(['Retail', 'Insurance', 'Commercial'] as const).map((type) => (
                <Button
                  key={type}
                  variant="outline"
                  className="h-auto py-4 justify-start"
                  onClick={() => pendingConvertLeadId && performConvertLead(pendingConvertLeadId, type)}
                >
                  <div className="text-left">
                    <div className="font-semibold">{type}</div>
                    <div className="text-xs text-muted-foreground">
                      {type === 'Retail' && 'Standard homeowner-paid job'}
                      {type === 'Insurance' && 'Insurance claim with adjuster workflow'}
                      {type === 'Commercial' && 'Commercial property project'}
                    </div>
                  </div>
                </Button>
              ))}
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setPendingConvertLeadId(null)}>Cancel</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AppLayout>;
};
export default SalesCommandCenter;